/**
 * Design system component: ItemCard
 *
 * Photo-first card used in the 2–3 column tablet/desktop grid layout
 * (rendered via `ItemGrid`). Shows a 4:3 photo or category emoji fallback,
 * a StatusDot overlay in the top-left corner, item name, category chip,
 * owner → borrower chain, and an optional due-date chip.
 *
 * On hover the card lifts slightly (translateY -2px) and shadow escalates
 * from `SHADOW.sm` to `SHADOW.lg` for tactile depth feedback.
 * Keyboard-accessible: responds to Enter and Space key presses.
 */
import { memo, useState } from 'react';
import { T, CAT_FALLBACK, CATEGORY_EMOJI, RADIUS, SPACE, SHADOW } from '@/lib/tokens';
import { StatusDot } from './StatusDot';
import { CategoryChip } from './CategoryChip';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import type { ItemWithOwner } from '@/hooks/useItems';
import { canViewBorrowerDetails } from '@/lib/loanVisibility';
import { DueDateBadge, getDueStatus } from './DueDateBadge';

const formatDue = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

interface ItemCardProps {
  item: ItemWithOwner;
  currentUserId: string;
  onClick: () => void;
  groupId?: string;
}

export const ItemCard = memo(function ItemCard({
  item,
  currentUserId,
  onClick,
  groupId,
}: ItemCardProps) {
  const [hovered, setHovered] = useState(false);

  const catColours = item.category && T.cat[item.category] ? T.cat[item.category] : CAT_FALLBACK;
  const emoji = item.category ? (CATEGORY_EMOJI[item.category] ?? '📦') : '📦';
  const { url: photoUrl } = useSignedUrl(item.photo_url);

  const borrowerIsCurrentUser = item.active_loan?.borrower_id === currentUserId;
  const statusState = !item.active_loan
    ? 'available'
    : borrowerIsCurrentUser
      ? 'with-you'
      : 'on-loan';

  const loanMasked = !canViewBorrowerDetails(item.active_loan, groupId);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        appearance: 'none',
        font: 'inherit',
        textAlign: 'left' as const,
        padding: 0,
        borderRadius: RADIUS.lg,
        border: `1px solid ${T.border}`,
        background: T.surface,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        boxShadow: hovered ? SHADOW.lg : SHADOW.md,
        transform: hovered ? 'translateY(-2px)' : 'none',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
      }}
    >
      {/* Photo area */}
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          background: photoUrl ? T.surfaceSubtle : catColours.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={item.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <span style={{ fontSize: 48, opacity: 0.7 }}>{emoji}</span>
        )}

        {/* Status badge overlay */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
          }}
        >
          <StatusDot state={statusState} />
        </div>
      </div>

      {/* Info area */}
      <div style={{ padding: `${SPACE.cardPad - 4}px ${SPACE.cardPad}px ${SPACE.cardPad}px` }}>
        {/* Item name + category chip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: T.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {item.name}
          </span>
          {item.category && <CategoryChip category={item.category} />}
          {item.is_giving_away && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.03em',
                padding: '3px 9px',
                borderRadius: 6,
                background: T.accentSoft,
                color: T.accent,
                border: `1px solid ${T.accentBorder}`,
                flexShrink: 0,
              }}
            >
              Giving Away
            </span>
          )}
        </div>

        {/* Owner + holder row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            {item.owner_name ?? 'Unknown'}
            {item.active_loan?.borrower_name && !loanMasked && (
              <>
                <span style={{ margin: '0 3px', color: T.border }}>{' \u2192 '}</span>
                <span style={{ color: T.textSec }}>
                  {item.active_loan.borrower_id === currentUserId
                    ? 'You'
                    : item.active_loan.borrower_name}
                </span>
              </>
            )}
            {item.active_loan && loanMasked && (
              <>
                <span style={{ margin: '0 3px', color: T.border }}>{' \u2192 '}</span>
                <span style={{ color: T.textMuted, fontStyle: 'italic' }}>On loan</span>
              </>
            )}
          </span>
        </div>

        {/* Due date chip: borrower sees future dates + overdue badge; owner sees overdue badge only */}
        {!loanMasked && item.active_loan?.due_at && (
          <div style={{ marginTop: 6 }}>
            {borrowerIsCurrentUser && getDueStatus(item.active_loan.due_at) === 'ok' && (
              <span
                style={{
                  fontSize: 11,
                  padding: '1px 6px',
                  borderRadius: 4,
                  fontWeight: 600,
                  background: T.surfaceSubtle,
                  color: T.textMuted,
                }}
              >
                Due {formatDue(item.active_loan.due_at)}
              </span>
            )}
            {(borrowerIsCurrentUser || item.owner_id === currentUserId) && (
              <DueDateBadge dueAt={item.active_loan.due_at} />
            )}
          </div>
        )}
      </div>
    </button>
  );
});
